import { useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchMFExecutionData } from '../../../../repository/mfExecution'
import { useLoading } from '../../../../state-management/contexts/LoadingContext'
import type { MFExecutionData } from '../../../../types/mf-execution.types'
import { postCTA } from '../../../../utils/appBridge'
import type { LumpsumAllocationPlansResponse } from '../lumpsum/api/allocation.types'
import type { LumpsumIdealPlansResponse } from '../lumpsum/api/ideal.types'
import type { LumpsumRebalancePlansResponse } from '../lumpsum/api/rebalance.types'
import { PlansPlanMode } from '../lumpsum/api/types'

/** Same key as MF L2 `MFExecutionProvider` so plans CTAs reuse cache. */
export const MF_EXECUTION_DATA_QUERY_KEY = 'mfExecutionData'

const PLAN_CTA_MISSING_TOAST = 'Unable to continue right now. Please try again.'
const PLAN_EXECUTION_ERROR_TOAST = 'Something went wrong. Please try again.'

export type ShowToastFn = (params: { message: string; duration?: number }) => void

export type PlanFundActionHookOptions = {
  transactionCta: unknown
  isRecommendationSheetSource?: boolean
  analyticsScreen?: string
}

export function resolvePlanTransactionCta({
  planMode,
  allocationPlansData,
  rebalancePlansData,
  idealData
}: {
  planMode: PlansPlanMode
  allocationPlansData?: LumpsumAllocationPlansResponse
  rebalancePlansData?: LumpsumRebalancePlansResponse
  idealData?: LumpsumIdealPlansResponse
}): unknown {
  if (planMode === PlansPlanMode.REBALANCE) {
    return rebalancePlansData?.transactionCta
  }
  if (planMode === PlansPlanMode.IDEAL_PORTFOLIO) {
    return idealData?.transactionCta
  }
  return allocationPlansData?.transactionCta
}

/** Imperative validations fetch: shared React Query cache + global loading via onSettled. */
export function useFetchFundExecutionWithLoading() {
  const queryClient = useQueryClient()
  const { showLoading, hideLoading } = useLoading()

  const { mutateAsync } = useMutation({
    mutationFn: (isin: string) =>
      queryClient.fetchQuery({
        queryKey: [MF_EXECUTION_DATA_QUERY_KEY, isin],
        queryFn: () => fetchMFExecutionData(isin)
      }),
    onMutate: (isin) => {
      if (queryClient.getQueryData([MF_EXECUTION_DATA_QUERY_KEY, isin]) == null) {
        showLoading()
      }
    },
    onSettled: () => {
      hideLoading()
    }
  })

  return async (isin: string): Promise<MFExecutionData | null> => {
    if (!isin) {
      return null
    }
    try {
      return await mutateAsync(isin)
    } catch {
      return null
    }
  }
}

export function postPlanCta(cta: unknown, showToast: ShowToastFn): void {
  if (cta == null) {
    showToast({ message: PLAN_CTA_MISSING_TOAST, duration: 2500 })
    return
  }
  try {
    postCTA(JSON.stringify(cta))
  } catch {
    showToast({ message: PLAN_CTA_MISSING_TOAST, duration: 2500 })
  }
}

export function postFilledSipPlanCta(
  cta: unknown,
  inputData: { isin: string; amount?: string },
  showToast: ShowToastFn
): void {
  if (cta == null || typeof cta !== 'object') {
    postPlanCta(null, showToast)
    return
  }

  const filledCta = {
    ...(cta as Record<string, unknown>),
    requestPayload: {
      inputData
    }
  }
  console.log('[plans-v4] filled execution CTA', filledCta)
  postPlanCta(filledCta, showToast)
}

export function toastFundExecutionError(showToast: ShowToastFn): void {
  showToast({ message: PLAN_EXECUTION_ERROR_TOAST, duration: 2500 })
}
